import React, { useState, useCallback, useMemo } from "react";

export function useSortableBoard(columns) {
  // 🔧 Safely extract a readable label from `col?.name` (which can be string, array, or React element)
const dragCardInitData = useMemo(() => {
  return columns
    ?.filter((item) => !item?.omit)
    ?.map((col) => {
      let labelValue;

      // 🟢 Special case
      if (col.id === "mar_as_read") {
        labelValue = "Mark As Read";
      }

      // 🟢 If col?.name is an array (like in your data1)
      else if (Array.isArray(col?.name)) {
        const firstItem = col?.name[0];
        if (typeof firstItem === "string") {
          labelValue = firstItem;
        } else if (React.isValidElement(firstItem)) {
          labelValue = firstItem;
        } else if (firstItem?.props?.children) {
          labelValue = firstItem.props.children;
        } else {
          labelValue = String(firstItem);
        }
      }

      // 🟢 If col?.name is a React element directly
      else if (React.isValidElement(col?.name)) {
        const children = col?.name?.props?.children;
        if (Array.isArray(children)) {
          const firstChild = children[0];
          labelValue =
            typeof firstChild === "string"
              ? firstChild
              : firstChild?.props?.children || "";
        } else if (typeof children === "string") {
          labelValue = children;
        } else if (children?.props?.children) {
          labelValue = children.props.children;
        } else {
          labelValue = String(col?.name);
        }
      }

      // 🟢 Otherwise, plain string or primitive
      else {
        labelValue = String(col?.name ?? "");
      }

      return {
        id: col.id,
        label: labelValue,
        checked: true,
      };
    });
}, [columns]);


  const [board, setBoard] = useState({
    columns: [
      {
        id: 1,
        cards: [...dragCardInitData],
      },
    ],
  });

  const removeMultipleCards = (keysToRemove, keyField = "id") => {
    setBoard((prevBoard) => ({
      ...prevBoard,
      columns: prevBoard.columns.map((column) => ({
        ...column,
        cards: column.cards.filter(
          (card) => !keysToRemove.includes(card[keyField])
        ),
      })),
    }));
  };

  const updateHeaderBoard = useCallback(
    (fieldName) => {
      let items = board.columns[0]?.cards || [];
      const currentIndex = columns?.findIndex((item) => item?.id === fieldName);
      const currentDict = columns?.find((item) => item?.id === fieldName);

      const exists = items?.some((i) => i.id === fieldName);
      if (!exists && currentDict && currentIndex !== -1) {
        const insertItem = {
          id: currentDict.id,
          label: currentDict.name,
          checked: true,
        };
        items.splice(currentIndex, 0, insertItem);
        setBoard({
          columns: [
            {
              id: 1,
              cards: [...items],
            },
          ],
        });
      }
    },
    [board, columns]
  );

  const computedColumns = useMemo(() => {
    if (!board?.columns?.[0]?.cards) return columns;

    const items = board.columns[0].cards;
    const checkedItemIds = items.filter((item) => item.checked).map((item) => item.id);

    return columns
      .filter((col) =>
        items.some((item) => item.id === col.id && item.checked)
      )
      .sort((a, b) => checkedItemIds.indexOf(a.id) - checkedItemIds.indexOf(b.id));
  }, [board, columns]);

  const handleClickResetsortableHeading = useCallback(() => {
    setBoard({
      columns: [
        {
          id: 1,
          cards: [...dragCardInitData],
        },
      ],
    });
  }, [dragCardInitData]);

  return {
    board,
    setBoard,
    handleClickResetsortableHeading,
    dragCardInitData,
    computedColumns,
    removeMultipleCards,
    updateHeaderBoard,
  };
}
